class VideosSlider {
  constructor(sliderEl) {
    this.slider = sliderEl;
    this.track = sliderEl.querySelector('.videos-slider__track');
    this.slides = Array.from(sliderEl.querySelectorAll('.video-slide'));
    this.prevBtn = sliderEl.querySelector('.videos-slider__btn--prev');
    this.nextBtn = sliderEl.querySelector('.videos-slider__btn--next');
    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.dragMoved = false;
    this.dragStarted = false;

    if (!this.track || this.slides.length === 0) return;

    this.init();
  }

  init() {
    this.updateSlidesPerView();
    this.updateActiveSlide();
    this.updateButtons();

    this.prevBtn.addEventListener('click', () => this.prev());
    this.nextBtn.addEventListener('click', () => this.next());

    // Touch events
    this.track.addEventListener('touchstart', this.touchStart.bind(this), { passive: true });
    this.track.addEventListener('touchmove', this.touchMove.bind(this), { passive: true });
    this.track.addEventListener('touchend', this.touchEnd.bind(this));

    // Mouse drag events - only on track, not on product links
    this.track.addEventListener('mousedown', this.dragStart.bind(this));
    document.addEventListener('mousemove', this.dragMove.bind(this));
    document.addEventListener('mouseup', this.dragEnd.bind(this));

    // Video cover play buttons
    this.slides.forEach((slide) => {
      const cover = slide.querySelector('.video-cover');
      const iframe = slide.querySelector('.video-iframe[data-video-iframe]');
      const playBtn = slide.querySelector('.video-play-btn');

      if (cover && iframe && playBtn) {
        const playVideo = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const embedUrl = cover.getAttribute('data-video-embed');
          if (embedUrl) {
            const separator = embedUrl.indexOf('?') !== -1 ? '&' : '?';
            iframe.src = embedUrl + separator + 'autoplay=1&mute=1';
            iframe.style.display = 'block';
            cover.classList.add('hidden');
          } else {
            const src = iframe.src;
            if (src && src.indexOf('autoplay=1') === -1) {
              const sep = src.indexOf('?') !== -1 ? '&' : '?';
              iframe.src = src + sep + 'autoplay=1&mute=1';
            }
            cover.classList.add('hidden');
          }
        };
        cover.addEventListener('click', playVideo);
        playBtn.addEventListener('click', playVideo);
      }
    });

    window.addEventListener('resize', () => {
      this.updateSlidesPerView();
      this.updateButtons();
      this.setPositionByIndex(this.currentIndex);
      this.updateActiveSlide();
    });
  }

  getSlidesPerView() {
    const width = this.slider.offsetWidth;
    if (width >= 990) {
      return parseInt(getComputedStyle(this.slider).getPropertyValue('--vs-desktop-slides')) || 3;
    } else if (width >= 768) {
      return parseInt(getComputedStyle(this.slider).getPropertyValue('--vs-tablet-slides')) || 2;
    } else {
      return parseInt(getComputedStyle(this.slider).getPropertyValue('--vs-mobile-slides')) || 1;
    }
  }

  updateSlidesPerView() {
    this.slidesPerView = this.getSlidesPerView();
    this.maxIndex = Math.max(0, this.slides.length - this.slidesPerView);
    if (this.currentIndex > this.maxIndex) {
      this.currentIndex = this.maxIndex;
    }
  }

  getSlideWidth() {
    const gap = parseFloat(getComputedStyle(this.slider).getPropertyValue('--vs-slide-gap')) || 16;
    const containerWidth = this.slider.offsetWidth;
    return (containerWidth - (this.slidesPerView - 1) * gap) / this.slidesPerView + gap;
  }

  setPositionByIndex(index) {
    const slideWidth = this.getSlideWidth();
    this.currentTranslate = -index * slideWidth;
    this.prevTranslate = this.currentTranslate;
    this.track.style.transition = 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  getActiveIndex() {
    const centerOffset = Math.floor(this.slidesPerView / 2);
    return Math.min(this.currentIndex + centerOffset, this.slides.length - 1);
  }

  updateActiveSlide() {
    const activeIdx = this.getActiveIndex();
    this.slides.forEach((slide, index) => {
      if (index === activeIdx) {
        slide.classList.add('is-active');
      } else {
        slide.classList.remove('is-active');
      }
    });
  }

  updateButtons() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex <= 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
    }
  }

  next() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.setPositionByIndex(this.currentIndex);
      this.updateActiveSlide();
      this.updateButtons();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.setPositionByIndex(this.currentIndex);
      this.updateActiveSlide();
      this.updateButtons();
    }
  }

  isInteractiveElement(target) {
    return target.closest('a') || target.closest('button') || target.closest('input') || target.closest('textarea');
  }

  touchStart(e) {
    if (this.isInteractiveElement(e.target)) return;
    this.isDragging = true;
    this.dragMoved = false;
    this.startX = e.touches[0].clientX;
    this.track.style.transition = 'none';
  }

  touchMove(e) {
    if (!this.isDragging) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - this.startX;
    if (Math.abs(diff) > 5) this.dragMoved = true;
    this.currentTranslate = this.prevTranslate + diff;
    this.track.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  touchEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    const movedBy = this.currentTranslate - this.prevTranslate;
    const slideWidth = this.getSlideWidth();
    const threshold = slideWidth * 0.2;

    if (movedBy < -threshold && this.currentIndex < this.maxIndex) {
      this.currentIndex++;
    } else if (movedBy > threshold && this.currentIndex > 0) {
      this.currentIndex--;
    }

    this.setPositionByIndex(this.currentIndex);
    this.updateActiveSlide();
    this.updateButtons();
  }

  dragStart(e) {
    if (this.isInteractiveElement(e.target)) return;
    if (e.button !== 0) return;
    e.preventDefault();
    this.dragStarted = true;
    this.isDragging = false;
    this.dragMoved = false;
    this.startX = e.clientX;
    this.track.style.transition = 'none';
    this.track.style.cursor = 'grabbing';
  }

  dragMove(e) {
    if (!this.dragStarted) return;
    const currentX = e.clientX;
    const diff = currentX - this.startX;

    if (!this.isDragging && Math.abs(diff) > 5) {
      this.isDragging = true;
      this.dragMoved = true;
    }

    if (this.isDragging) {
      this.currentTranslate = this.prevTranslate + diff;
      this.track.style.transform = `translateX(${this.currentTranslate}px)`;
    }
  }

  dragEnd() {
    if (!this.dragStarted) return;
    this.dragStarted = false;
    this.track.style.cursor = 'grab';

    if (!this.isDragging) return;
    this.isDragging = false;

    const movedBy = this.currentTranslate - this.prevTranslate;
    const slideWidth = this.getSlideWidth();
    const threshold = slideWidth * 0.2;

    if (movedBy < -threshold && this.currentIndex < this.maxIndex) {
      this.currentIndex++;
    } else if (movedBy > threshold && this.currentIndex > 0) {
      this.currentIndex--;
    }

    this.setPositionByIndex(this.currentIndex);
    this.updateActiveSlide();
    this.updateButtons();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-videos-slider]').forEach((slider) => {
    new VideosSlider(slider);
  });
});
